import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { HelpCircle, ExternalLink, Clock, ArrowDown, AlertCircle, RefreshCcw, Server } from "lucide-react";
import { Bar, BarChart, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip } from "recharts";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

interface Transaction {
  id: string;
  amount: number;
  buyer_id: string;
  chef_id: string;
  chef_payout: number;
  created_at: string;
  meal_id: string;
  order_id: string;
  payout_scheduled_date: string | null;
  payout_status: 'pending' | 'scheduled' | 'completed';
  platform_fee: number;
  refund_amount: number | null;
  refund_reason: string | null;
  refund_status: string | null;
  status: string;
  stripe_fee: number;
  stripe_payment_intent_id: string | null;
  stripe_transfer_id: string | null;
  updated_at: string;
}

interface PayoutStats {
  pending_amount: number;
  scheduled_amount: number;
  completed_amount: number;
  next_payout_date: string | null;
}

interface EarningsSectionProps {
  transactions: Transaction[] | undefined;
  chartData: any[];
  formatNumber: (num: number) => string;
  isLoading: boolean;
}

export const EarningsSection = ({
  transactions,
  chartData,
  formatNumber,
  isLoading
}: EarningsSectionProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [showDebugDialog, setShowDebugDialog] = useState(false);
  const [systemDiagnostics, setSystemDiagnostics] = useState<any>(null);

  const { data: payoutData, isLoading: isLoadingPayouts, error: payoutError, refetch } = useQuery({
    queryKey: ['chef-payout-stats', retryCount],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      try {
        console.log("Calling check-payouts Edge Function");
        const { data, error } = await supabase.functions.invoke('check-payouts', {
          body: { userId: user.id }
        });

        if (error) {
          console.error('Edge function error:', error);
          setErrorDetails(`Edge function error: ${error.message || 'Unknown error'}`);
          throw new Error(`Edge function error: ${error.message || 'Unknown error'}`);
        }
        
        if (!data) {
          console.error('No data returned from server');
          setErrorDetails('No data returned from server');
          throw new Error('No data returned from server');
        }
        
        console.log("Raw payout data:", data);
        
        if (!data.stats) {
          console.error('Invalid response format - missing stats:', data);
          setErrorDetails(`Invalid response format - missing stats: ${JSON.stringify(data)}`);
          return {
            stats: {
              pending_amount: 0,
              scheduled_amount: 0,
              completed_amount: 0,
              next_payout_date: null
            },
            payoutSettings: {
              payout_delay_days: 3
            }
          };
        }
        
        const stats: PayoutStats = {
          pending_amount: typeof data.stats.pending_amount === 'number' ? data.stats.pending_amount : 0,
          scheduled_amount: typeof data.stats.scheduled_amount === 'number' ? data.stats.scheduled_amount : 0,
          completed_amount: typeof data.stats.completed_amount === 'number' ? data.stats.completed_amount : 0,
          next_payout_date: data.stats.next_payout_date
        };
        
        setSystemDiagnostics({
          cronJobs: data.cronJobs || [],
          serviceRoleKeyConfigured: data.serviceRoleKeyConfigured || false,
          transactionCount: data.transactionCount || 0
        });
        
        console.log("Successfully received payout data with stats:", stats);
        return {
          ...data,
          stats
        };
      } catch (err) {
        console.error('Error fetching payout stats:', err);
        setHasError(true);
        throw err;
      }
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000
  });

  const payoutStats: PayoutStats = payoutData?.stats || {
    pending_amount: 0,
    scheduled_amount: 0, 
    completed_amount: 0,
    next_payout_date: null
  };

  useEffect(() => {
    if (payoutData && hasError) {
      setHasError(false);
      setErrorDetails(null);
    }
  }, [payoutData, hasError]);

  const upcomingEarnings = !isLoadingPayouts 
    ? payoutStats.pending_amount + payoutStats.scheduled_amount
    : 0;
    
  const totalEarnings = !isLoadingPayouts 
    ? payoutStats.completed_amount + upcomingEarnings
    : 0;

  const refreshPayoutStatus = async () => {
    try {
      setIsRefreshing(true);
      setHasError(false);
      setErrorDetails(null);
      
      await refetch();
      
      queryClient.invalidateQueries({ queryKey: ['chef-transactions'] });
      
      toast({
        title: "Data Refreshed",
        description: "Your earnings data has been updated.",
      });
    } catch (err) {
      console.error('Error refreshing payout status:', err);
      setHasError(true);
      toast({
        title: "Update Failed",
        description: "Could not update payout status. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    setHasError(false);
    setErrorDetails(null);
    setIsRefreshing(true);
    
    refetch().then(() => {
      toast({
        title: "Retry Successful",
        description: "Your earnings data has been updated.",
      });
    }).catch((err) => {
      console.error('Error retrying payout fetch:', err);
      toast({
        title: "Retry Failed",
        description: "Could not update payout status. Please try again later.",
        variant: "destructive"
      });
    }).finally(() => {
      setIsRefreshing(false);
    });
  };

  const triggerPayoutFunction = async (functionName: string) => {
    try {
      setIsRefreshing(true);
      setHasError(false);
      setErrorDetails(null);
      
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) {
        throw new Error('Not authenticated');
      }
      
      const { data, error } = await supabase.functions.invoke('check-payouts', {
        body: { 
          userId: userData.user.id,
          manualTrigger: {
            functionName,
            payload: { manual: true, chefId: userData.user.id }
          }
        }
      });
      
      if (error) {
        console.error(`Error triggering function:`, error);
        throw error;
      }
      
      if (data?.triggerResult?.errorDetails?.length > 0) {
        const errorMessage = data.triggerResult.errorDetails[0]?.error || 'An error occurred during processing';
        console.error('Function error details:', data.triggerResult.errorDetails);
        setErrorDetails(`Operation completed with errors: ${errorMessage}`);
        
        toast({
          title: "Operation Completed with Errors",
          description: errorMessage,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: `Successfully triggered ${functionName}`,
        });
      }
      
      await refetch();
      
    } catch (err) {
      console.error(`Error in payout operation:`, err);
      setHasError(true);
      setErrorDetails(err instanceof Error ? err.message : 'Unknown error');
      toast({
        title: "Operation Failed",
        description: `Could not perform operation: ${err instanceof Error ? err.message : 'Unknown error'}`,
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const formatPayoutDate = (dateString: string | null) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {hasError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span>There was an error loading your earnings data.</span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleRetry}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <Clock className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {isRefreshing ? 'Retrying...' : 'Retry'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDebugDialog(true)}
                >
                  <Server className="h-3.5 w-3.5 mr-1.5" />
                  Troubleshoot
                </Button>
              </div>
            </div>
            {errorDetails && (
              <div className="text-xs bg-red-100 p-2 rounded">
                {errorDetails}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between">
              {isLoadingPayouts ? (
                <Skeleton className="h-10 w-32" />
              ) : (
                <h2 className="text-3xl font-bold text-black">${formatNumber(upcomingEarnings)}</h2>
              )}
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={refreshPayoutStatus}
                disabled={isRefreshing}
                className="text-xs"
              >
                {isRefreshing ? (
                  <>
                    <Clock className="h-3 w-3 mr-1.5 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <ArrowDown className="h-3 w-3 mr-1.5" />
                    Refresh
                  </>
                )}
              </Button>
            </div>
            
            <p className="text-black">Upcoming earnings</p>
            
            {isLoadingPayouts ? (
              <Skeleton className="h-5 w-48 mt-1" />
            ) : payoutStats.next_payout_date ? (
              <p className="text-sm text-green-600 mt-1 flex items-center">
                <Clock className="h-3.5 w-3.5 mr-1" />
                Next payout: {formatPayoutDate(payoutStats.next_payout_date)}
              </p>
            ) : upcomingEarnings > 0 ? (
              <p className="text-sm text-black mt-1">
                Scheduled for processing in the next {payoutData?.payoutSettings?.payout_delay_days || 3} business days
              </p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                No pending payouts at this time
              </p>
            )}
          </div>

          <div className="mt-6">
            {isLoadingPayouts ? (
              <Skeleton className="h-8 w-40" />
            ) : (
              <p className="text-xl font-semibold text-black">
                ${formatNumber(totalEarnings)} earned
              </p>
            )}
            <p className="text-sm text-black">Total earnings</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div style={{ width: '100%', height: '60vh', minHeight: '400px' }}>
          {isLoading || isLoadingPayouts ? (
            <div className="w-full h-full flex items-center justify-center">
              <Skeleton className="w-full h-full" />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ChartContainer
                config={{
                  completed: {
                    label: "Previous earnings",
                    theme: {
                      light: "#818cf8",
                      dark: "#818cf8"
                    }
                  },
                  scheduled: {
                    label: "Scheduled earnings",
                    theme: {
                      light: "#4f46e5",
                      dark: "#4f46e5"
                    }
                  },
                  pending: {
                    label: "Pending earnings",
                    theme: {
                      light: "#c7d2fe",
                      dark: "#c7d2fe"
                    }
                  }
                }}
              >
                <BarChart data={chartData} margin={{ top: 20, right: 20, bottom: 20, left: 40 }}>
                  <XAxis 
                    dataKey="date" 
                    stroke="#000000"
                    tick={{ fill: '#000000' }}
                    axisLine={{ stroke: '#000000' }}
                    tickLine={{ stroke: '#000000' }}
                  />
                  <YAxis 
                    stroke="#000000"
                    tick={{ fill: '#000000' }}
                    tickFormatter={(value) => `$${formatNumber(value)}`}
                    axisLine={{ stroke: '#000000' }}
                    tickLine={{ stroke: '#000000' }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Bar
                    dataKey="completed"
                    stackId="1"
                    fill="#818cf8"
                  />
                  <Bar
                    dataKey="scheduled"
                    stackId="1"
                    fill="#4f46e5"
                  />
                  <Bar
                    dataKey="pending"
                    stackId="1"
                    fill="#c7d2fe"
                  />
                </BarChart>
              </ChartContainer>
            </ResponsiveContainer>
          )}
        </div>

        <div className="mt-6 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-indigo-400" />
            <div className="flex-1">
              <span className="text-sm text-black">Previous earnings</span>
            </div>
            <TooltipProvider delayDuration={0}>
              <Tooltip defaultOpen={false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 hover:bg-transparent"
                    aria-label="Learn more about previous earnings"
                  >
                    <HelpCircle className="h-4 w-4 text-black hover:text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  <p className="text-sm">Earnings from past meals. These earnings have already been paid out.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isLoadingPayouts ? (
              <Skeleton className="ml-4 h-5 w-20" />
            ) : (
              <span className="ml-4 font-mono text-black">
                ${formatNumber(payoutStats.completed_amount)}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-indigo-600" />
            <div className="flex-1">
              <span className="text-sm text-black">Scheduled earnings</span>
            </div>
            <TooltipProvider delayDuration={0}>
              <Tooltip defaultOpen={false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 hover:bg-transparent"
                    aria-label="Learn more about scheduled earnings"
                  >
                    <HelpCircle className="h-4 w-4 text-black hover:text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  <p className="text-sm">Earnings scheduled for payout in the next few days.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isLoadingPayouts ? (
              <Skeleton className="ml-4 h-5 w-20" />
            ) : (
              <span className="ml-4 font-mono text-black">
                ${formatNumber(payoutStats.scheduled_amount)}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded bg-indigo-200" />
            <div className="flex-1">
              <span className="text-sm text-black">Pending earnings</span>
            </div>
            <TooltipProvider delayDuration={0}>
              <Tooltip defaultOpen={false}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 hover:bg-transparent"
                    aria-label="Learn more about pending earnings"
                  >
                    <HelpCircle className="h-4 w-4 text-black hover:text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="left" align="center">
                  <p className="text-sm">Earnings waiting for the {payoutData?.payoutSettings?.payout_delay_days || 3}-day holding period before being scheduled for payout.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {isLoadingPayouts ? (
              <Skeleton className="ml-4 h-5 w-20" />
            ) : (
              <span className="ml-4 font-mono text-black">
                ${formatNumber(payoutStats.pending_amount)}
              </span>
            )}
          </div>
          
          <div className="pt-4 border-t">
            <a 
              href="https://express.stripe.com/account" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
            >
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              View detailed payment history in Stripe
            </a>
          </div>
        </div>
      </Card>

      <Dialog open={showDebugDialog} onOpenChange={setShowDebugDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>System Diagnostics</DialogTitle>
            <DialogDescription>
              Troubleshooting information for payout system
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <h4 className="font-medium">Actions</h4>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => triggerPayoutFunction('schedule-payouts')}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Schedule Now
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => triggerPayoutFunction('process-scheduled-payouts')}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Process Now
                </Button>
              </div>
            </div>
            
            {errorDetails && (
              <div className="space-y-2">
                <h4 className="font-medium">Error Details</h4>
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                  {errorDetails}
                </div>
              </div>
            )}
            
            {systemDiagnostics && (
              <>
                <div className="space-y-2">
                  <h4 className="font-medium">System Status</h4>
                  <div className="rounded-md bg-muted p-3 text-sm">
                    <p>Service Role Key: {systemDiagnostics.serviceRoleKeyConfigured ? 'Configured' : 'Missing'}</p>
                    <p>Transaction Count: {systemDiagnostics.transactionCount}</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-medium">Recent Cron Jobs</h4>
                  {systemDiagnostics.cronJobs.length > 0 ? (
                    <div className="text-sm space-y-2">
                      {systemDiagnostics.cronJobs.map((job: any, idx: number) => (
                        <div 
                          key={idx} 
                          className={`rounded-md p-3 text-xs ${job.metadata.success ? 'bg-green-50' : 'bg-red-50'}`}
                        >
                          <p><strong>Job:</strong> {job.metadata.job_name}</p>
                          <p><strong>Status:</strong> {job.metadata.success ? 'Success' : 'Failed'}</p>
                          <p><strong>Time:</strong> {new Date(job.timestamp).toLocaleString()}</p>
                          {job.metadata.error && (
                            <p className="text-red-500"><strong>Error:</strong> {job.metadata.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-md bg-amber-50 p-3 text-sm">
                      No cron job records found. This may indicate the scheduled tasks are not running.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button onClick={handleRetry} variant="secondary" disabled={isRefreshing}>
              {isRefreshing ? <Clock className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCcw className="h-4 w-4 mr-2" />}
              Refresh Data
            </Button>
            <Button onClick={() => setShowDebugDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
